import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Beef, LogOut, Home, Save, Eye, Palette, Type, Layout, Image, RotateCcw, Globe, CreditCard, Megaphone, MapPin, Clock, Phone, MessageCircle, ShoppingBag, Truck, Package, FolderOpen, Smartphone, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUploader from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const FONT_OPTIONS = [
  'Playfair Display', 'Inter', 'Poppins', 'Roboto', 'Lora', 'Montserrat',
  'Oswald', 'Merriweather', 'Bebas Neue', 'Dancing Script', 'Raleway', 'Lobster'
];

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});


const SECTION_FIELDS = {
  brand: ['business_name', 'phone_number', 'tagline', 'logo_url'],
  colors: [
    'primary_color', 'secondary_color',
    'order_primary_color', 'order_bg_color', 'order_text_color',
    'card_bg_color', 'card_bg_gradient_end', 'card_accent_color', 'card_text_color'
  ],
  landing: [
    'hero_title', 'hero_description', 'hero_image', 'about_title', 'about_text', 'about_image', 'footer_text',
    'home_show_hero', 'home_show_quick_actions', 'home_show_categories', 'home_show_offers',
    'home_show_about', 'home_show_contact', 'home_show_install_button',
    'home_hero_button_text', 'home_quick_actions_title', 'home_categories_title',
    'home_offers_title', 'home_about_button_text', 'home_section_order'
  ],
  order: [
    'order_primary_color', 'order_bg_color', 'order_text_color',
    'order_heading_font', 'order_body_font', 'order_title', 'order_subtitle',
    'order_show_categories', 'order_show_images', 'order_show_notes', 'price_rounding_step'
  ],
  contact: [
    'address', 'phone', 'whatsapp', 'show_whatsapp', 'email', 'maps_url',
    'instagram', 'facebook', 'tiktok',
    'show_instagram', 'show_facebook', 'show_tiktok', 'opening_hours',
    'order_hours',
    'delivery_enabled', 'delivery_fee', 'delivery_min_order_amount', 'delivery_hours', 'delivery_zones'
  ],
  card: [
    'card_bg_color', 'card_bg_gradient_end', 'card_accent_color', 'card_text_color',
    'card_logo_url', 'card_background_image', 'card_logo_position',
    'card_points_position', 'card_show_total_points', 'card_expiry_message',
    'card_show_qr_preview'
  ],
  points: ['euros_per_point', 'points_for_discount', 'discount_value']
};

const SECTION_SAVE_LABELS = {
  brand: 'Brand & Logo',
  colors: 'Aspetto & Colori',
  landing: 'Landing Page',
  order: 'Pagina Ordini',
  contact: 'Contatti & Orari',
  card: 'Fidelity Card',
  points: 'Sistema Punti'
};

const defaultSettings = {
  // Business Info
  business_name: 'Macelleria Borgese',
  phone_number: '+39 123 456 7890',
  tagline: 'Qualità e tradizione dal 1950',
  
  // Site Colors - Amaranto e Oro
  primary_color: '#9B1B30',
  secondary_color: '#D4AF37',
  
  // Logo
  logo_url: '',
  
  // Landing Page
  hero_title: 'La Tua Fidelity Card Digitale',
  hero_description: 'Ogni acquisto ti avvicina a premi esclusivi. Accumula punti con ogni spesa e goditi sconti sulla carne di qualità che ami.',
  hero_image: 'https://images.pexels.com/photos/30649013/pexels-photo-30649013.jpeg',
  about_title: 'Tradizione e Qualità',
  about_text: 'Da generazioni, la Macelleria Borgese porta sulle tavole delle famiglie italiane la migliore carne selezionata con cura e passione.',
  about_image: 'https://images.pexels.com/photos/2079684/pexels-photo-2079684.jpeg',
  footer_text: 'Qualità e tradizione dal 1950',
  home_show_hero: true,
  home_show_quick_actions: true,
  home_show_categories: true,
  home_show_offers: true,
  home_show_about: true,
  home_show_contact: true,
  home_show_install_button: true,
  home_hero_button_text: 'Ordina ora',
  home_quick_actions_title: 'Accessi rapidi',
  home_categories_title: 'Scegli una categoria',
  home_offers_title: 'Offerte del momento',
  home_about_button_text: 'Scopri di più',
  home_section_order: ['hero', 'quick_actions', 'categories', 'offers', 'about', 'contact'],
  
  // Contact Info
  address: 'Via Roma 123, 00100 Roma',
  phone: '+39 06 1234567',
  whatsapp: '+39 333 1234567',
  show_whatsapp: true,
  email: 'info@macelleriaborg.it',
  maps_url: 'https://maps.google.com/?q=Via+Roma+123+Roma',
  
  // Social Media
  instagram: '',
  facebook: '',
  tiktok: '',
  show_instagram: true,
  show_facebook: true,
  show_tiktok: true,
  
  // Opening Hours
  opening_hours: {
    mon: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    tue: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    wed: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    thu: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    fri: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    sat: { open: '08:00', close: '13:00', afternoon_open: '', afternoon_close: '' },
    sun: { open: '', close: '', afternoon_open: '', afternoon_close: '' }
  },

  // Pickup order hours, independent from shop opening hours
  order_hours: {
    mon: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    tue: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    wed: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    thu: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    fri: { open: '08:00', close: '13:00', afternoon_open: '16:00', afternoon_close: '19:30' },
    sat: { open: '08:00', close: '13:00', afternoon_open: '', afternoon_close: '' },
    sun: { open: '', close: '', afternoon_open: '', afternoon_close: '' }
  },

  // Home delivery
  delivery_enabled: false,
  delivery_fee: 0,
  delivery_min_order_amount: 20,
  delivery_zones: [
    { id: 'rosarno', name: 'Rosarno', fee: 0, enabled: true },
    { id: 'san-ferdinando', name: 'San Ferdinando', fee: 0, enabled: true },
    { id: 'bosco', name: 'Bosco', fee: 0, enabled: true }
  ],
  delivery_hours: {
    mon: { open: '', close: '', afternoon_open: '', afternoon_close: '' },
    tue: { open: '', close: '', afternoon_open: '', afternoon_close: '' },
    wed: { open: '', close: '', afternoon_open: '', afternoon_close: '' },
    thu: { open: '', close: '', afternoon_open: '', afternoon_close: '' },
    fri: { open: '', close: '', afternoon_open: '', afternoon_close: '' },
    sat: { open: '', close: '', afternoon_open: '', afternoon_close: '' },
    sun: { open: '', close: '', afternoon_open: '', afternoon_close: '' }
  },
  
  // Fidelity Card - Amaranto e Oro
  card_bg_color: '#9B1B30',
  card_bg_gradient_end: '#6B1220',
  card_accent_color: '#D4AF37',
  card_text_color: '#FFFFFF',
  card_logo_url: '',
  card_background_image: '',
  card_logo_position: 'top-right',
  card_points_position: 'bottom-left',
  card_show_total_points: true,
  card_expiry_message: 'I punti scadono ogni fine mese',
  card_show_qr_preview: true,
  
  // Points System
  euros_per_point: 10,
  points_for_discount: 10,
  discount_value: 5,

  // Order Page appearance
  order_primary_color: '',
  order_bg_color: '#F9F7F2',
  order_text_color: '#2C2C2C',
  order_heading_font: 'Playfair Display',
  order_body_font: 'Inter',
  order_title: 'Prenota il Tuo Ordine',
  order_subtitle: 'Scegli i prodotti e ritirali in negozio',
  order_show_categories: true,
  order_show_images: true,
  order_show_notes: true,
  price_rounding_step: 0.10
};

export default function SiteEditor({ user, onLogout }) {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('brand');
  const [activeSchedule, setActiveSchedule] = useState('opening_hours');

  useEffect(() => {
    fetchSettings();
  }, []);

  // Load all selectable fonts so the editor previews render correctly
  useEffect(() => {
    const id = 'site-editor-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?' +
      FONT_OPTIONS.map(f => 'family=' + f.trim().replace(/ /g, '+') + ':wght@400;500;600;700').join('&') +
      '&display=swap';
    document.head.appendChild(link);
  }, []);

  const applyLoadedSettings = (data) => {
    setSettings({
      ...defaultSettings,
      ...(data || {}),
      opening_hours: {
        ...defaultSettings.opening_hours,
        ...((data && data.opening_hours) || {})
      },
      order_hours: {
        ...defaultSettings.order_hours,
        ...((data && data.order_hours) || (data && data.opening_hours) || {})
      },
      delivery_hours: {
        ...defaultSettings.delivery_hours,
        ...((data && data.delivery_hours) || {})
      }
    });
  };

  const homeSectionLabels = {
    hero: 'Prima schermata',
    quick_actions: 'Accessi rapidi',
    categories: 'Categorie',
    offers: 'Offerte',
    about: 'Chi siamo',
    contact: 'Orari e contatti'
  };

  const moveHomeSection = (index, direction) => {
    const order = Array.isArray(settings.home_section_order)
      ? [...settings.home_section_order]
      : [...defaultSettings.home_section_order];
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];
    handleChange('home_section_order', order);
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      applyLoadedSettings(response.data);
    } catch (error) {
      toast.error('Impossibile caricare le impostazioni salvate');
      console.error(error);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const fields = SECTION_FIELDS[activeSection] || [];
    const sectionPayload = Object.fromEntries(fields.map(field => [field, settings[field]]));

    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, sectionPayload, getAuthHeaders());
      if (response.data?.settings) applyLoadedSettings(response.data.settings);
      toast.success(`${SECTION_SAVE_LABELS[activeSection]} salvato correttamente!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio della sezione');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const label = SECTION_SAVE_LABELS[activeSection];
    if (!window.confirm(`Ripristinare solo la sezione "${label}" ai valori predefiniti? Le altre sezioni non verranno toccate.`)) return;

    const fields = SECTION_FIELDS[activeSection] || [];
    setSettings(prev => {
      const next = { ...prev };
      fields.forEach(field => {
        next[field] = ['opening_hours', 'order_hours', 'delivery_hours'].includes(field)
          ? JSON.parse(JSON.stringify(defaultSettings[field]))
          : defaultSettings[field];
      });
      return next;
    });
    toast.info(`${label} ripristinato nell'editor. Premi Salva per confermare.`);
  };

  const sections = [
    { id: 'products', icon: Package, label: 'Prodotti' },
    { id: 'categories', icon: FolderOpen, label: 'Categorie' },
    { id: 'brand', icon: Beef, label: 'Brand & Logo' },
    { id: 'colors', icon: Palette, label: 'Colori' },
    { id: 'landing', icon: Globe, label: 'Landing Page' },
    { id: 'order', icon: ShoppingBag, label: 'Pagina Ordini' },
    { id: 'contact', icon: MapPin, label: 'Contatti & Orari' },
    { id: 'card', icon: CreditCard, label: 'Fidelity Card' },
    { id: 'points', icon: Megaphone, label: 'Sistema Punti' }
  ];
  
  const dayNames = {
    mon: 'Lunedì',
    tue: 'Martedì', 
    wed: 'Mercoledì',
    thu: 'Giovedì',
    fri: 'Venerdì',
    sat: 'Sabato',
    sun: 'Domenica'
  };
  
  const handleScheduleChange = (scheduleName, day, field, value) => {
    setSettings(prev => ({
      ...prev,
      [scheduleName]: {
        ...prev[scheduleName],
        [day]: {
          ...prev[scheduleName]?.[day],
          [field]: value
        }
      }
    }));
  };

  const handleHoursChange = (day, field, value) => {
    handleScheduleChange('opening_hours', day, field, value);
  };

  const handlePickupHoursChange = (day, field, value) => {
    handleScheduleChange('order_hours', day, field, value);
  };

  const handleDeliveryHoursChange = (day, field, value) => {
    handleScheduleChange('delivery_hours', day, field, value);
  };

  const copyOpeningHoursToPickup = () => {
    setSettings(prev => ({
      ...prev,
      order_hours: JSON.parse(JSON.stringify(prev.opening_hours || defaultSettings.opening_hours))
    }));
    toast.info('Orari di apertura copiati nei ritiri. Premi Salva per confermare.');
  };

  const copyOpeningHoursToDelivery = () => {
    setSettings(prev => ({
      ...prev,
      delivery_hours: JSON.parse(JSON.stringify(prev.opening_hours || defaultSettings.opening_hours))
    }));
    toast.info('Orari di apertura copiati nella consegna. Premi Salva per confermare.');
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="site-editor">
      {/* Header */}
      <header style={{ backgroundColor: settings.primary_color }} className="text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center gap-3">
              <Beef className="w-8 h-8" style={{ color: settings.secondary_color }} />
              <div>
                <h1 className="text-xl font-serif font-bold">Editor Sito Completo</h1>
                <p className="text-xs" style={{ color: settings.secondary_color }}>Personalizza tutto</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!['products', 'categories'].includes(activeSection) && (
              <>
            <button
              onClick={handleReset}
              className="text-white/80 hover:text-white flex items-center gap-2 text-sm"
            >
              <RotateCcw size={18} />
              <span className="hidden sm:inline">Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-[#2C2C2C] px-6 py-2 rounded-full font-semibold transition-colors flex items-center gap-2"
              style={{ backgroundColor: settings.secondary_color }}
            >
              <Save size={18} />
              {saving ? 'Salvataggio...' : `Salva ${SECTION_SAVE_LABELS[activeSection]}`}
            </button>
              </>
            )}
            <button onClick={onLogout} className="text-white/80 hover:text-white ml-2">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Section Navigation */}
        <div className="flex gap-2 flex-wrap mb-8">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                activeSection === section.id
                  ? 'text-white shadow-lg'
                  : 'bg-white hover:opacity-80 card-shadow'
              }`}
              style={activeSection === section.id 
                ? { backgroundColor: settings.primary_color } 
                : { color: settings.primary_color }
              }
            >
              <section.icon size={20} />
              {section.label}
            </button>
          ))}
        </div>

        {/* Products Section */}
        {activeSection === 'products' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-5 sm:p-8 card-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#5D1822] text-white flex items-center justify-center mb-5">
                <Package size={28} />
              </div>
              <h2 className="font-serif text-2xl font-bold mb-2" style={{ color: settings.primary_color }}>
                Catalogo prodotti
              </h2>
              <p className="text-[#5A5A5A] mb-6">
                Crea e modifica prodotti, foto, prezzi, unità, disponibilità e categoria.
              </p>
              <Link to="/admin/products" className="inline-flex min-h-[48px] px-6 rounded-xl text-white font-bold items-center justify-center" style={{ backgroundColor: settings.primary_color }}>
                Apri Catalogo Prodotti
              </Link>
            </div>
          </div>
        )}

        {/* Categories Section */}
        {activeSection === 'categories' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-5 sm:p-8 card-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#C5A059] text-white flex items-center justify-center mb-5">
                <FolderOpen size={28} />
              </div>
              <h2 className="font-serif text-2xl font-bold mb-2" style={{ color: settings.primary_color }}>
                Categorie prodotti
              </h2>
              <p className="text-[#5A5A5A] mb-6">
                Crea, rinomina, ordina e nascondi le categorie visualizzate nell’app.
              </p>
              <Link to="/admin/categories" className="inline-flex min-h-[48px] px-6 rounded-xl text-white font-bold items-center justify-center" style={{ backgroundColor: settings.primary_color }}>
                Apri Editor Categorie
              </Link>
            </div>
          </div>
        )}

        {/* Brand & Logo Section */}
        {activeSection === 'brand' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Beef size={24} />
                Informazioni Attività
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Nome Attività
                  </label>
                  <input
                    type="text"
                    value={settings.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    className="input-default w-full text-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Numero di Telefono
                  </label>
                  <input
                    type="text"
                    value={settings.phone_number}
                    onChange={(e) => handleChange('phone_number', e.target.value)}
                    className="input-default w-full"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Slogan / Tagline
                  </label>
                  <input
                    type="text"
                    value={settings.tagline}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                    className="input-default w-full"
                    placeholder="Es: Qualità e tradizione dal 1950"
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Image size={24} />
                Logo
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  URL Logo (opzionale)
                </label>
                <input
                  type="url"
                  value={settings.logo_url || ''}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  className="input-default w-full"
                  placeholder="https://esempio.com/logo.png"
                />
                <p className="text-xs text-[#5A5A5A] mt-2">
                  Inserisci l'URL di un'immagine per il logo. Lascia vuoto per usare l'icona predefinita.
                </p>
                
                {/* Logo Preview */}
                <div className="mt-4 p-4 bg-[#F9F7F2] rounded-lg flex items-center gap-4">
                  <span className="text-sm text-[#5A5A5A]">Anteprima:</span>
                  {settings.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" className="h-12 object-contain" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Beef className="w-10 h-10" style={{ color: settings.primary_color }} />
                      <span className="font-serif font-bold" style={{ color: settings.primary_color }}>{settings.business_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Colors Section */}
        {activeSection === 'colors' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                  <h3 className="text-xl font-serif font-bold flex items-center gap-2" style={{ color: settings.primary_color }}>
                    <Palette size={24} />
                    Aspetto e colori del sito
                  </h3>
                  <p className="text-sm text-[#5A5A5A] mt-1">
                    Modifica i colori senza entrare nel codice. Le modifiche vengono salvate solo quando premi il pulsante in alto.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings(previous => ({
                    ...previous,
                    primary_color: defaultSettings.primary_color,
                    secondary_color: defaultSettings.secondary_color,
                    order_primary_color: defaultSettings.order_primary_color,
                    order_bg_color: defaultSettings.order_bg_color,
                    order_text_color: defaultSettings.order_text_color,
                    card_bg_color: defaultSettings.card_bg_color,
                    card_bg_gradient_end: defaultSettings.card_bg_gradient_end,
                    card_accent_color: defaultSettings.card_accent_color,
                    card_text_color: defaultSettings.card_text_color
                  }))}
                  className="px-4 py-2.5 rounded-xl border border-[#D8D2C8] font-semibold text-sm hover:bg-[#F9F7F2]"
                >
                  Ripristina colori predefiniti
                </button>
              </div>

              <h4 className="font-bold text-[#2C2C2C] mb-3">Colori generali</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: 'primary_color', label: 'Colore principale', hint: 'Header, pulsanti e titoli' },
                  { key: 'secondary_color', label: 'Colore secondario', hint: 'Accenti, icone e dettagli' }
                ].map(item => (
                  <label key={item.key} className="rounded-xl border border-[#E8E2D8] p-4">
                    <span className="block font-semibold text-[#2C2C2C]">{item.label}</span>
                    <span className="block text-xs text-[#5A5A5A] mb-3">{item.hint}</span>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={settings[item.key] || '#000000'}
                        onChange={event => handleChange(item.key, event.target.value)}
                        className="h-12 w-16 rounded-lg border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings[item.key] || ''}
                        onChange={event => handleChange(item.key, event.target.value)}
                        className="input-default flex-1 uppercase"
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <h4 className="font-bold text-[#2C2C2C] mb-1">Pagina Ordina</h4>
              <p className="text-sm text-[#5A5A5A] mb-4">
                Questi colori cambiano ricerca, categorie, pulsanti, sfondo e testi dell’app per ordinare.
              </p>

              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { key: 'order_primary_color', label: 'Pulsanti e categorie', fallback: settings.primary_color },
                  { key: 'order_bg_color', label: 'Sfondo pagina', fallback: '#F9F7F2' },
                  { key: 'order_text_color', label: 'Testi', fallback: '#2C2C2C' }
                ].map(item => (
                  <label key={item.key} className="rounded-xl border border-[#E8E2D8] p-4">
                    <span className="block font-semibold text-[#2C2C2C] mb-3">{item.label}</span>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings[item.key] || item.fallback}
                        onChange={event => handleChange(item.key, event.target.value)}
                        className="h-11 w-14 rounded-lg border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings[item.key] || ''}
                        placeholder={item.fallback}
                        onChange={event => handleChange(item.key, event.target.value)}
                        className="input-default min-w-0 flex-1 uppercase"
                      />
                    </div>
                  </label>
                ))}
              </div>

              <div
                className="mt-5 rounded-2xl border overflow-hidden"
                style={{
                  backgroundColor: settings.order_bg_color,
                  color: settings.order_text_color
                }}
              >
                <div className="p-4 border-b border-black/10">
                  <div
                    className="h-11 rounded-xl flex items-center px-4 text-white font-semibold"
                    style={{ backgroundColor: settings.order_primary_color || settings.primary_color }}
                  >
                    Cerca un prodotto...
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex gap-2 mb-4 overflow-hidden">
                    {['Vitella', 'Maiale', 'Pollo'].map(label => (
                      <span
                        key={label}
                        className="px-3 py-2 rounded-xl text-white text-sm font-semibold"
                        style={{ backgroundColor: settings.order_primary_color || settings.primary_color }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  <div className="rounded-xl bg-white border p-3 flex items-center justify-between gap-3">
                    <div>
                      <strong className="block">Cotoletta di vitella</strong>
                      <span className="text-sm">€14,99/kg</span>
                    </div>
                    <button
                      type="button"
                      className="px-3 py-2 rounded-lg text-white text-sm font-semibold"
                      style={{ backgroundColor: settings.order_primary_color || settings.primary_color }}
                    >
                      Aggiungi
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <h4 className="font-bold text-[#2C2C2C] mb-1">Fidelity Card</h4>
              <p className="text-sm text-[#5A5A5A] mb-4">
                Personalizza la tessera digitale visualizzata dai clienti.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: 'card_bg_color', label: 'Sfondo iniziale' },
                  { key: 'card_bg_gradient_end', label: 'Sfondo finale' },
                  { key: 'card_accent_color', label: 'Colore accento' },
                  { key: 'card_text_color', label: 'Colore testo' }
                ].map(item => (
                  <label key={item.key} className="rounded-xl border border-[#E8E2D8] p-4">
                    <span className="block font-semibold text-[#2C2C2C] mb-3">{item.label}</span>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings[item.key]}
                        onChange={event => handleChange(item.key, event.target.value)}
                        className="h-11 w-14 rounded-lg border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings[item.key]}
                        onChange={event => handleChange(item.key, event.target.value)}
                        className="input-default min-w-0 flex-1 uppercase"
                      />
                    </div>
                  </label>
                ))}
              </div>

              <div
                className="mt-5 rounded-2xl p-5 min-h-[160px] flex flex-col justify-between shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${settings.card_bg_color}, ${settings.card_bg_gradient_end})`,
                  color: settings.card_text_color
                }}
              >
                <div className="flex items-center justify-between">
                  <strong className="font-serif text-xl">{settings.business_name}</strong>
                  <Beef size={30} style={{ color: settings.card_accent_color }} />
                </div>
                <div>
                  <span className="text-sm opacity-80">Punti disponibili</span>
                  <div className="text-3xl font-bold" style={{ color: settings.card_accent_color }}>120</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <h4 className="font-bold text-[#2C2C2C] mb-3">Temi pronti</h4>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                  { name: 'Borgese', primary: '#5D1822', secondary: '#C5A059', bg: '#F9F7F2', text: '#2C2C2C' },
                  { name: 'Amaranto', primary: '#9B1B30', secondary: '#D4AF37', bg: '#FFF8F4', text: '#2C2C2C' },
                  { name: 'Elegante', primary: '#1A1A2E', secondary: '#E94560', bg: '#F5F5F8', text: '#20202A' },
                  { name: 'Naturale', primary: '#2D5A27', secondary: '#A4C639', bg: '#F7FAF4', text: '#233020' },
                  { name: 'Nero e oro', primary: '#1A1A1A', secondary: '#FFD700', bg: '#F6F4EF', text: '#1A1A1A' }
                ].map(theme => (
                  <button
                    type="button"
                    key={theme.name}
                    onClick={() => setSettings(previous => ({
                      ...previous,
                      primary_color: theme.primary,
                      secondary_color: theme.secondary,
                      order_primary_color: theme.primary,
                      order_bg_color: theme.bg,
                      order_text_color: theme.text,
                      card_bg_color: theme.primary,
                      card_bg_gradient_end: theme.primary,
                      card_accent_color: theme.secondary,
                      card_text_color: '#FFFFFF'
                    }))}
                    className="rounded-xl border-2 border-[#E5E5E5] p-3 hover:border-[#5D1822] transition-all"
                  >
                    <div className="flex justify-center gap-1 mb-2">
                      <span className="w-7 h-7 rounded-full" style={{ backgroundColor: theme.primary }} />
                      <span className="w-7 h-7 rounded-full" style={{ backgroundColor: theme.secondary }} />
                      <span className="w-7 h-7 rounded-full border" style={{ backgroundColor: theme.bg }} />
                    </div>
                    <span className="text-sm font-semibold">{theme.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Landing Page Section */}
        {activeSection === 'landing' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Layout size={24} />
                Struttura Home da telefono
              </h3>
              <p className="text-sm text-[#5A5A5A] mt-2">
                Mostra o nascondi le sezioni e scegli il loro ordine nella schermata iniziale.
              </p>

              <div className="mt-5 space-y-2">
                {(settings.home_section_order || defaultSettings.home_section_order).map((sectionId, index) => {
                  const visibilityField = {
                    hero: 'home_show_hero',
                    quick_actions: 'home_show_quick_actions',
                    categories: 'home_show_categories',
                    offers: 'home_show_offers',
                    about: 'home_show_about',
                    contact: 'home_show_contact'
                  }[sectionId];

                  return (
                    <div key={sectionId} className="rounded-xl border border-[#E5DED5] bg-[#FAF8F4] p-3 flex items-center gap-3">
                      <GripVertical size={18} className="text-[#9A9187]" />
                      <strong className="flex-1 text-sm">{homeSectionLabels[sectionId] || sectionId}</strong>
                      <label className="flex items-center gap-2 text-xs font-semibold">
                        <input
                          type="checkbox"
                          checked={settings[visibilityField] !== false}
                          onChange={(e) => handleChange(visibilityField, e.target.checked)}
                          className="w-5 h-5"
                        />
                        Visibile
                      </label>
                      <div className="flex flex-col gap-1">
                        <button type="button" onClick={() => moveHomeSection(index, -1)} disabled={index === 0} className="w-8 h-7 rounded-lg bg-white border flex items-center justify-center disabled:opacity-30">
                          <ChevronUp size={16} />
                        </button>
                        <button type="button" onClick={() => moveHomeSection(index, 1)} disabled={index === (settings.home_section_order || defaultSettings.home_section_order).length - 1} className="w-8 h-7 rounded-lg bg-white border flex items-center justify-center disabled:opacity-30">
                          <ChevronDown size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <label className="mt-4 rounded-xl border border-[#E5DED5] p-3 flex items-center justify-between gap-3">
                <span className="font-semibold text-sm">Mostra pulsante “Installa app”</span>
                <input type="checkbox" checked={settings.home_show_install_button !== false} onChange={(e) => handleChange('home_show_install_button', e.target.checked)} className="w-5 h-5" />
              </label>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Type size={24} />
                Testi Home
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                {[
                  ['home_hero_button_text', 'Testo pulsante principale'],
                  ['home_quick_actions_title', 'Titolo accessi rapidi'],
                  ['home_categories_title', 'Titolo categorie'],
                  ['home_offers_title', 'Titolo offerte'],
                  ['home_about_button_text', 'Testo collegamento Chi siamo']
                ].map(([field, label]) => (
                  <label key={field}>
                    <span className="block text-sm font-medium mb-2">{label}</span>
                    <input type="text" value={settings[field] || ''} onChange={(e) => handleChange(field, e.target.value)} className="input-default w-full" />
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-[#EFE9E0] rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-serif font-bold flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Smartphone size={22} />
                Anteprima struttura telefono
              </h3>
              <div className="mt-4 mx-auto max-w-[300px] rounded-[28px] bg-[#F7F3ED] border-[6px] border-[#29231F] overflow-hidden shadow-xl">
                <div className="h-7 bg-[#29231F]" />
                <div className="p-3 space-y-2 min-h-[430px]">
                  {(settings.home_section_order || defaultSettings.home_section_order).map(sectionId => {
                    const visible = {
                      hero: settings.home_show_hero !== false,
                      quick_actions: settings.home_show_quick_actions !== false,
                      categories: settings.home_show_categories !== false,
                      offers: settings.home_show_offers !== false,
                      about: settings.home_show_about !== false,
                      contact: settings.home_show_contact !== false
                    }[sectionId];
                    if (!visible) return null;
                    const heights = { hero: 'h-28', quick_actions: 'h-16', categories: 'h-16', offers: 'h-20', about: 'h-20', contact: 'h-16' };
                    return (
                      <div key={sectionId} className={`${heights[sectionId]} rounded-xl bg-white border border-[#E5DED5] p-2`}>
                        <span className="text-[10px] font-bold" style={{ color: settings.primary_color }}>
                          {homeSectionLabels[sectionId]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Globe size={24} />
                Sezione Hero (Prima Schermata)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Titolo Principale
                  </label>
                  <input
                    type="text"
                    value={settings.hero_title}
                    onChange={(e) => handleChange('hero_title', e.target.value)}
                    className="input-default w-full text-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Descrizione
                  </label>
                  <textarea
                    value={settings.hero_description}
                    onChange={(e) => handleChange('hero_description', e.target.value)}
                    className="input-default w-full h-24 resize-none"
                  />
                </div>

                <ImageUploader
                  label="Grafica Prima Schermata"
                  currentImage={settings.hero_image}
                  onImageChange={(url) => handleChange('hero_image', url)}
                  isDark={false}
                />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Type size={24} />
                Sezione "Chi Siamo"
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Titolo Sezione
                  </label>
                  <input
                    type="text"
                    value={settings.about_title}
                    onChange={(e) => handleChange('about_title', e.target.value)}
                    className="input-default w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Testo Descrittivo
                  </label>
                  <textarea
                    value={settings.about_text}
                    onChange={(e) => handleChange('about_text', e.target.value)}
                    className="input-default w-full h-32 resize-none"
                  />
                </div>

                <ImageUploader
                  label="Grafica Sezione Chi Siamo"
                  currentImage={settings.about_image}
                  onImageChange={(url) => handleChange('about_image', url)}
                  isDark={false}
                />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6" style={{ color: settings.primary_color }}>
                Footer
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Testo Footer
                </label>
                <input
                  type="text"
                  value={settings.footer_text}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  className="input-default w-full"
                />
              </div>
            </div>
          </div>
        )}

        {/* Order Page Section */}
        {activeSection === 'order' && (
          <div className="space-y-6" data-testid="order-editor-section">
            {/* Texts */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Type size={24} />
                Testi Pagina Ordini
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Titolo</label>
                  <input
                    type="text"
                    value={settings.order_title || ''}
                    onChange={(e) => handleChange('order_title', e.target.value)}
                    className="input-default w-full"
                    data-testid="order-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Sottotitolo</label>
                  <input
                    type="text"
                    value={settings.order_subtitle || ''}
                    onChange={(e) => handleChange('order_subtitle', e.target.value)}
                    className="input-default w-full"
                    data-testid="order-subtitle-input"
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Palette size={24} />
                Colori Pagina Ordini
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Colore Principale (bottoni)</label>
                  <div className="flex gap-2">
                    <input type="color" value={settings.order_primary_color || settings.primary_color} onChange={(e) => handleChange('order_primary_color', e.target.value)} className="h-12 w-16 rounded-lg border cursor-pointer" data-testid="order-primary-color" />
                    <input type="text" value={settings.order_primary_color || ''} onChange={(e) => handleChange('order_primary_color', e.target.value)} placeholder="Usa colore sito" className="input-default flex-1 text-sm" />
                  </div>
                  <p className="text-xs text-[#5A5A5A] mt-1">Lascia vuoto per usare il colore del sito</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Sfondo Pagina</label>
                  <div className="flex gap-2">
                    <input type="color" value={settings.order_bg_color || '#F9F7F2'} onChange={(e) => handleChange('order_bg_color', e.target.value)} className="h-12 w-16 rounded-lg border cursor-pointer" data-testid="order-bg-color" />
                    <input type="text" value={settings.order_bg_color || ''} onChange={(e) => handleChange('order_bg_color', e.target.value)} className="input-default flex-1 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Colore Testo</label>
                  <div className="flex gap-2">
                    <input type="color" value={settings.order_text_color || '#2C2C2C'} onChange={(e) => handleChange('order_text_color', e.target.value)} className="h-12 w-16 rounded-lg border cursor-pointer" data-testid="order-text-color" />
                    <input type="text" value={settings.order_text_color || ''} onChange={(e) => handleChange('order_text_color', e.target.value)} className="input-default flex-1 text-sm" />
                  </div>
                </div>
              </div>
            </div>

            {/* Fonts */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Type size={24} />
                Font Pagina Ordini
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Font Titoli</label>
                  <select
                    value={settings.order_heading_font || 'Playfair Display'}
                    onChange={(e) => handleChange('order_heading_font', e.target.value)}
                    className="input-default w-full"
                    data-testid="order-heading-font"
                  >
                    {FONT_OPTIONS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                  </select>
                  <p className="mt-2 text-2xl" style={{ fontFamily: `'${settings.order_heading_font || 'Playfair Display'}', serif` }}>Macelleria Borgese</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Font Testo</label>
                  <select
                    value={settings.order_body_font || 'Inter'}
                    onChange={(e) => handleChange('order_body_font', e.target.value)}
                    className="input-default w-full"
                    data-testid="order-body-font"
                  >
                    {FONT_OPTIONS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                  </select>
                  <p className="mt-2 text-base" style={{ fontFamily: `'${settings.order_body_font || 'Inter'}', sans-serif` }}>Scegli i prodotti e ritirali in negozio.</p>
                </div>
              </div>
            </div>

            {/* Toggles: add / remove elements */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Layout size={24} />
                Elementi Visibili
              </h3>
              <div className="space-y-3">
                {[
                  { key: 'order_show_categories', label: 'Mostra filtri categoria' },
                  { key: 'order_show_images', label: 'Mostra immagini prodotti' },
                  { key: 'order_show_notes', label: 'Mostra campo note' }
                ].map(t => (
                  <div key={t.key} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-xl">
                    <span className="text-sm font-medium text-[#2C2C2C]">{t.label}</span>
                    <button
                      type="button"
                      onClick={() => handleChange(t.key, !(settings[t.key] !== false))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${settings[t.key] !== false ? '' : 'bg-gray-300'}`}
                      style={settings[t.key] !== false ? { backgroundColor: settings.primary_color } : {}}
                      data-testid={`toggle-${t.key}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings[t.key] !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>


            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-3" style={{ color: settings.primary_color }}>
                Arrotondamento prezzi
              </h3>
              <p className="text-sm text-[#5A5A5A] mb-4">
                Rende più puliti i totali dei prodotti venduti a peso.
              </p>
              <select
                value={String(settings.price_rounding_step ?? 0.10)}
                onChange={(e) => handleChange('price_rounding_step', Number(e.target.value))}
                className="input-default w-full md:w-80"
              >
                <option value="0">Nessun arrotondamento</option>
                <option value="0.1">Ai 10 centesimi</option>
                <option value="0.5">Ai 50 centesimi</option>
                <option value="1">All’euro</option>
              </select>
              <div className="mt-4 p-3 rounded-xl bg-[#F9F7F2] text-sm text-[#5A5A5A]">
                Esempio: €9,08 → €9,10 · €13,37 → €13,40
              </div>
            </div>

            {/* Live Preview */}
            <div className="rounded-xl p-6 card-shadow" style={{ backgroundColor: settings.order_bg_color || '#F9F7F2', color: settings.order_text_color || '#2C2C2C' }}>
              <p className="text-xs uppercase tracking-wide mb-3 opacity-60">Anteprima</p>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: `'${settings.order_heading_font || 'Playfair Display'}', serif`, color: settings.order_primary_color || settings.primary_color }}>
                {settings.order_title || 'Prenota il Tuo Ordine'}
              </h2>
              <p className="text-sm mb-4" style={{ fontFamily: `'${settings.order_body_font || 'Inter'}', sans-serif`, opacity: 0.7 }}>
                {settings.order_subtitle || 'Scegli i prodotti e ritirali in negozio'}
              </p>
              <button className="px-5 py-2 rounded-full text-white font-semibold" style={{ backgroundColor: settings.order_primary_color || settings.primary_color }}>
                Aggiungi
              </button>
            </div>
          </div>
        )}

        {/* Contact & Hours Section */}
        {activeSection === 'contact' && (          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Phone size={24} />
                Informazioni di Contatto
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Indirizzo
                  </label>
                  <input
                    type="text"
                    value={settings.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className="input-default w-full"
                    placeholder="Via Roma 123, 00100 Roma"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Telefono
                  </label>
                  <input
                    type="text"
                    value={settings.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="input-default w-full"
                    placeholder="+39 06 1234567"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2 flex items-center gap-2">
                    <MessageCircle size={16} className="text-green-600" />
                    WhatsApp
                  </label>
                  <input
                    type="text"
                    value={settings.whatsapp || ''}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    className="input-default w-full"
                    placeholder="+39 333 1234567"
                  />
                  <label className="mt-3 rounded-xl border border-[#E5DED5] bg-[#FAF8F4] px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                      <span className="block text-sm font-semibold">Mostra WhatsApp sul sito</span>
                      <span className="block text-xs text-[#6F675F] mt-0.5">
                        Disattivandolo il numero resta salvato, ma il pulsante non viene mostrato.
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.show_whatsapp !== false}
                      onChange={(e) => handleChange('show_whatsapp', e.target.checked)}
                      className="w-5 h-5 flex-shrink-0"
                    />
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={settings.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="input-default w-full"
                    placeholder="info@esempio.it"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Link Google Maps
                  </label>
                  <input
                    type="url"
                    value={settings.maps_url || ''}
                    onChange={(e) => handleChange('maps_url', e.target.value)}
                    className="input-default w-full"
                    placeholder="https://maps.google.com/?q=..."
                  />
                  <p className="text-xs text-[#5A5A5A] mt-2">
                    Inserisci il link di Google Maps per permettere ai clienti di trovarti facilmente
                  </p>
                </div>
              </div>
            </div>
            
            {/* Social Media */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
                📱 Social Media
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Instagram */}
                <div className="bg-[#F9F7F2] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2">
                      <span className="text-pink-500 text-lg">📷</span>
                      Instagram
                    </label>
                    <button
                      type="button"
                      onClick={() => handleChange('show_instagram', !settings.show_instagram)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${settings.show_instagram ? '' : 'bg-gray-300'}`}
                      style={settings.show_instagram ? { backgroundColor: settings.primary_color } : {}}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.show_instagram ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={settings.instagram || ''}
                    onChange={(e) => handleChange('instagram', e.target.value)}
                    className="input-default w-full text-sm"
                    placeholder="@tuoaccount"
                    disabled={!settings.show_instagram}
                  />
                </div>
                
                {/* Facebook */}
                <div className="bg-[#F9F7F2] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2">
                      <span className="text-blue-600 text-lg">📘</span>
                      Facebook
                    </label>
                    <button
                      type="button"
                      onClick={() => handleChange('show_facebook', !settings.show_facebook)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${settings.show_facebook ? '' : 'bg-gray-300'}`}
                      style={settings.show_facebook ? { backgroundColor: settings.primary_color } : {}}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.show_facebook ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={settings.facebook || ''}
                    onChange={(e) => handleChange('facebook', e.target.value)}
                    className="input-default w-full text-sm"
                    placeholder="facebook.com/tuapagina"
                    disabled={!settings.show_facebook}
                  />
                </div>
                
                {/* TikTok */}
                <div className="bg-[#F9F7F2] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2">
                      <span className="text-black text-lg">🎵</span>
                      TikTok
                    </label>
                    <button
                      type="button"
                      onClick={() => handleChange('show_tiktok', !settings.show_tiktok)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${settings.show_tiktok ? '' : 'bg-gray-300'}`}
                      style={settings.show_tiktok ? { backgroundColor: settings.primary_color } : {}}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.show_tiktok ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={settings.tiktok || ''}
                    onChange={(e) => handleChange('tiktok', e.target.value)}
                    className="input-default w-full text-sm"
                    placeholder="@tuoaccount"
                    disabled={!settings.show_tiktok}
                  />
                </div>
              </div>
              <p className="text-xs text-[#5A5A5A] mt-4">
                Usa i toggle per mostrare o nascondere i tuoi social nella landing page
              </p>
            </div>
            
            {/* Unified schedules */}
            <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow">
              <h3 className="text-xl font-serif font-bold flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Clock size={24} />
                Gestione orari
              </h3>
              <p className="text-sm text-[#5A5A5A] mt-1 mb-5">
                Usa una sola tabella e scegli sopra quali orari modificare.
              </p>

              <div className="grid grid-cols-3 gap-2 mb-5">
                {[
                  { id: 'opening_hours', label: 'Macelleria', icon: '🏪' },
                  { id: 'order_hours', label: 'Ritiri', icon: '🛍️' },
                  { id: 'delivery_hours', label: 'Consegne', icon: '🚚' }
                ].map(option => (
                  <button
                    type="button"
                    key={option.id}
                    onClick={() => setActiveSchedule(option.id)}
                    className={`min-h-[58px] rounded-xl border-2 px-2 py-2 font-bold text-sm flex flex-col sm:flex-row items-center justify-center gap-1.5 ${
                      activeSchedule === option.id ? 'text-white' : 'bg-white text-[#2C2C2C] border-[#DED8CF]'
                    }`}
                    style={activeSchedule === option.id ? { backgroundColor: settings.primary_color, borderColor: settings.primary_color } : {}}
                  >
                    <span className="text-lg">{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>

              {activeSchedule === 'delivery_hours' && (
                <div className="grid sm:grid-cols-3 gap-3 mb-5 rounded-xl bg-blue-50 border border-blue-200 p-4">
                  <label className="flex items-center gap-3 sm:col-span-3 font-bold">
                    <input type="checkbox" checked={Boolean(settings.delivery_enabled)} onChange={event => handleChange('delivery_enabled', event.target.checked)} className="w-6 h-6" />
                    Consegna a domicilio attiva
                  </label>
                  <label className="font-semibold text-sm">
                    Costo consegna (€)
                    <input type="number" min="0" step="0.50" value={settings.delivery_fee ?? 0} onChange={event => handleChange('delivery_fee', Math.max(0, Number(event.target.value) || 0))} className="input-default w-full mt-2" />
                  </label>
                  <label className="font-semibold text-sm">
                    Ordine minimo (€)
                    <input type="number" min="0" step="1" value={settings.delivery_min_order_amount ?? 20} onChange={event => handleChange('delivery_min_order_amount', Math.max(0, Number(event.target.value) || 0))} className="input-default w-full mt-2" />
                  </label>
                  <button type="button" onClick={copyOpeningHoursToDelivery} className="self-end min-h-[44px] rounded-xl border font-semibold text-sm" style={{ borderColor: settings.primary_color, color: settings.primary_color }}>
                    Copia orari macelleria
                  </button>
                  <div className="sm:col-span-3 rounded-xl bg-white border border-blue-200 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-bold">Paesi e tariffe di consegna</p>
                        <p className="text-xs text-[#6B655D]">Aggiungi i paesi serviti e imposta una tariffa diversa per ciascuno.</p>
                      </div>
                      <button type="button" onClick={() => handleChange('delivery_zones', [...(settings.delivery_zones || []), { id: `zona-${Date.now()}`, name: '', fee: 0, enabled: true }])} className="min-h-[40px] px-3 rounded-lg text-white font-bold text-sm" style={{ backgroundColor: settings.primary_color }}>
                        + Aggiungi paese
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(settings.delivery_zones || []).map((zone, index) => (
                        <div key={zone.id || index} className="grid grid-cols-1 sm:grid-cols-[auto_1fr_140px_auto] gap-2 items-center rounded-lg border border-[#E7E1D8] p-3">
                          <input type="checkbox" checked={zone.enabled !== false} onChange={event => { const zones = [...(settings.delivery_zones || [])]; zones[index] = { ...zones[index], enabled: event.target.checked }; handleChange('delivery_zones', zones); }} className="w-5 h-5" title="Paese attivo" />
                          <input type="text" placeholder="Nome del paese" value={zone.name || ''} onChange={event => { const zones = [...(settings.delivery_zones || [])]; zones[index] = { ...zones[index], name: event.target.value }; handleChange('delivery_zones', zones); }} className="input-default w-full" />
                          <label className="text-xs font-bold">Tariffa (€)
                            <input type="number" min="0" step="0.50" value={zone.fee ?? 0} onChange={event => { const zones = [...(settings.delivery_zones || [])]; zones[index] = { ...zones[index], fee: Math.max(0, Number(event.target.value) || 0) }; handleChange('delivery_zones', zones); }} className="input-default w-full mt-1" />
                          </label>
                          <button type="button" onClick={() => handleChange('delivery_zones', (settings.delivery_zones || []).filter((_, zoneIndex) => zoneIndex !== index))} className="min-h-[40px] px-3 rounded-lg border border-red-200 text-red-700 font-bold text-sm">Elimina</button>
                        </div>
                      ))}
                      {!(settings.delivery_zones || []).length && <p className="text-sm text-red-700 font-semibold">Aggiungi almeno un paese per usare la consegna.</p>}
                    </div>
                  </div>
                </div>
              )}

              {activeSchedule === 'order_hours' && (
                <div className="flex justify-end mb-4">
                  <button type="button" onClick={copyOpeningHoursToPickup} className="min-h-[44px] px-4 rounded-xl border font-semibold text-sm" style={{ borderColor: settings.primary_color, color: settings.primary_color }}>
                    Copia orari macelleria
                  </button>
                </div>
              )}

              <div className="space-y-3">
                {Object.entries(dayNames).map(([dayKey, dayName]) => {
                  const hours = settings[activeSchedule]?.[dayKey] || {};
                  const isClosed = !hours.open && !hours.afternoon_open;
                  const closedLabel = activeSchedule === 'opening_hours' ? 'Chiuso' : activeSchedule === 'order_hours' ? 'Nessun ritiro' : 'Nessuna consegna';

                  return (
                    <div key={`${activeSchedule}-${dayKey}`} className="rounded-xl bg-[#F9F7F2] border border-[#E8E2D8] p-3 sm:p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <strong style={{ color: settings.primary_color }}>{dayName}</strong>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isClosed}
                            onChange={event => {
                              if (event.target.checked) {
                                ['open', 'close', 'afternoon_open', 'afternoon_close'].forEach(field => handleScheduleChange(activeSchedule, dayKey, field, ''));
                              } else {
                                handleScheduleChange(activeSchedule, dayKey, 'open', activeSchedule === 'delivery_hours' ? '09:00' : '08:00');
                                handleScheduleChange(activeSchedule, dayKey, 'close', '13:00');
                              }
                            }}
                            className="w-5 h-5"
                          />
                          <span className="text-sm font-semibold text-red-600">{closedLabel}</span>
                        </label>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { title: 'Mattina', open: 'open', close: 'close' },
                          { title: 'Pomeriggio', open: 'afternoon_open', close: 'afternoon_close' }
                        ].map(range => (
                          <div key={range.title}>
                            <label className="block text-xs font-semibold text-[#5A5A5A] mb-1.5">{range.title}</label>
                            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                              <input type="time" value={hours[range.open] || ''} onChange={event => handleScheduleChange(activeSchedule, dayKey, range.open, event.target.value)} className="input-default min-w-0" />
                              <span>–</span>
                              <input type="time" value={hours[range.close] || ''} onChange={event => handleScheduleChange(activeSchedule, dayKey, range.close, event.target.value)} className="input-default min-w-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                Stai modificando: <strong>{activeSchedule === 'opening_hours' ? 'orari della macelleria' : activeSchedule === 'order_hours' ? 'orari dei ritiri' : 'orari delle consegne'}</strong>.
                Al termine premi <strong>Salva Contatti &amp; Orari</strong>.
              </div>
            </div>
          </div>
        )}

        {/* Fidelity Card Section */}
        {activeSection === 'card' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Card Preview */}
            <div>
              <h3 className="text-xl font-serif font-bold mb-4 flex items-center gap-2" style={{ color: settings.primary_color }}>
                <Eye size={24} />
                Anteprima Card
              </h3>
              
              <div 
                className="rounded-2xl p-8 text-white card-shadow relative overflow-hidden"
                style={{
                  background: settings.card_background_image 
                    ? `linear-gradient(135deg, ${settings.card_bg_color}dd, ${settings.card_bg_gradient_end}dd), url(${settings.card_background_image})`
                    : `linear-gradient(135deg, ${settings.card_bg_color}, ${settings.card_bg_gradient_end})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  color: settings.card_text_color
                }}
              >
                <div 
                  className="absolute top-0 right-0 w-64 h-64 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10"
                  style={{ backgroundColor: settings.card_accent_color }}
                ></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <p className="text-sm mb-1" style={{ color: settings.card_accent_color }}>Fidelity Card</p>
                      <h3 className="text-xl font-serif font-bold">{settings.business_name}</h3>
                    </div>
                    <Beef className="w-12 h-12" style={{ color: settings.card_accent_color }} />
                  </div>
                  
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-sm mb-2 opacity-70">Punti Attuali</p>
                      <p className="text-5xl font-mono font-bold" style={{ color: settings.card_accent_color }}>42</p>
                    </div>
                    {settings.card_show_total_points && (
                      <div className="text-right">
                        <p className="text-sm mb-2 opacity-70">Totale</p>
                        <p className="text-2xl font-mono font-bold">156</p>
                      </div>
                    )}
                  </div>
                  
                  {settings.card_expiry_message && (
                    <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-sm">{settings.card_expiry_message}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Card Settings */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 card-shadow">
                <h4 className="font-semibold mb-4" style={{ color: settings.primary_color }}>Colori Card</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#5A5A5A] mb-1">Sfondo (Inizio)</label>
                    <div className="flex gap-2">
                      <input type="color" value={settings.card_bg_color} onChange={(e) => handleChange('card_bg_color', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                      <input type="text" value={settings.card_bg_color} onChange={(e) => handleChange('card_bg_color', e.target.value)} className="input-default flex-1 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5A5A5A] mb-1">Sfondo (Fine)</label>
                    <div className="flex gap-2">
                      <input type="color" value={settings.card_bg_gradient_end} onChange={(e) => handleChange('card_bg_gradient_end', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                      <input type="text" value={settings.card_bg_gradient_end} onChange={(e) => handleChange('card_bg_gradient_end', e.target.value)} className="input-default flex-1 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5A5A5A] mb-1">Accento</label>
                    <div className="flex gap-2">
                      <input type="color" value={settings.card_accent_color} onChange={(e) => handleChange('card_accent_color', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                      <input type="text" value={settings.card_accent_color} onChange={(e) => handleChange('card_accent_color', e.target.value)} className="input-default flex-1 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5A5A5A] mb-1">Testo</label>
                    <div className="flex gap-2">
                      <input type="color" value={settings.card_text_color} onChange={(e) => handleChange('card_text_color', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                      <input type="text" value={settings.card_text_color} onChange={(e) => handleChange('card_text_color', e.target.value)} className="input-default flex-1 text-sm" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 card-shadow">
                <h4 className="font-semibold mb-4" style={{ color: settings.primary_color }}>Immagine Sfondo Card</h4>
                <input
                  type="url"
                  value={settings.card_background_image || ''}
                  onChange={(e) => handleChange('card_background_image', e.target.value)}
                  className="input-default w-full mb-3"
                  placeholder="URL immagine sfondo"
                />
                <div className="grid grid-cols-4 gap-2">
                  {[
                    'https://images.pexels.com/photos/1639557/pexels-photo-1639557.jpeg?auto=compress&cs=tinysrgb&w=400',
                    'https://images.pexels.com/photos/618775/pexels-photo-618775.jpeg?auto=compress&cs=tinysrgb&w=400',
                    'https://images.pexels.com/photos/65175/pexels-photo-65175.jpeg?auto=compress&cs=tinysrgb&w=400',
                    ''
                  ].map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleChange('card_background_image', url)}
                      className={`aspect-video rounded-lg overflow-hidden border-2 ${settings.card_background_image === url ? 'border-[#5D1822]' : 'border-[#E5E5E5]'}`}
                    >
                      {url ? (
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center text-white text-xs">No</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 card-shadow">
                <h4 className="font-semibold mb-4" style={{ color: settings.primary_color }}>Opzioni</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={settings.card_show_total_points} onChange={(e) => handleChange('card_show_total_points', e.target.checked)} className="w-5 h-5 rounded" />
                    <span className="text-sm">Mostra totale punti guadagnati</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={settings.card_show_qr_preview} onChange={(e) => handleChange('card_show_qr_preview', e.target.checked)} className="w-5 h-5 rounded" />
                    <span className="text-sm">Mostra anteprima QR</span>
                  </label>
                </div>
                <div className="mt-4">
                  <label className="block text-xs text-[#5A5A5A] mb-1">Messaggio scadenza punti</label>
                  <input
                    type="text"
                    value={settings.card_expiry_message}
                    onChange={(e) => handleChange('card_expiry_message', e.target.value)}
                    className="input-default w-full text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Points System Section */}
        {activeSection === 'points' && (
          <div className="bg-white rounded-xl p-6 card-shadow">
            <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2" style={{ color: settings.primary_color }}>
              <Megaphone size={24} />
              Sistema Punti e Sconti
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[#F9F7F2] rounded-xl p-6 text-center">
                <label className="block text-sm font-medium text-[#5A5A5A] mb-3">
                  Euro per 1 Punto
                </label>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-2xl">€</span>
                  <input
                    type="number"
                    value={settings.euros_per_point}
                    onChange={(e) => handleChange('euros_per_point', parseFloat(e.target.value))}
                    className="input-default w-24 text-center text-2xl font-bold"
                    min="1"
                  />
                </div>
                <p className="text-xs text-[#5A5A5A] mt-2">Ogni €{settings.euros_per_point} spesi = 1 punto</p>
              </div>
              
              <div className="bg-[#F9F7F2] rounded-xl p-6 text-center">
                <label className="block text-sm font-medium text-[#5A5A5A] mb-3">
                  Punti per Sconto
                </label>
                <input
                  type="number"
                  value={settings.points_for_discount}
                  onChange={(e) => handleChange('points_for_discount', parseInt(e.target.value))}
                  className="input-default w-24 text-center text-2xl font-bold mx-auto"
                  min="1"
                />
                <p className="text-xs text-[#5A5A5A] mt-2">{settings.points_for_discount} punti = 1 sconto</p>
              </div>
              
              <div className="bg-[#F9F7F2] rounded-xl p-6 text-center">
                <label className="block text-sm font-medium text-[#5A5A5A] mb-3">
                  Valore Sconto
                </label>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-2xl">€</span>
                  <input
                    type="number"
                    value={settings.discount_value}
                    onChange={(e) => handleChange('discount_value', parseFloat(e.target.value))}
                    className="input-default w-24 text-center text-2xl font-bold"
                    min="1"
                  />
                </div>
                <p className="text-xs text-[#5A5A5A] mt-2">Sconto di €{settings.discount_value}</p>
              </div>
            </div>
            
            {/* Summary */}
            <div className="mt-8 text-white rounded-xl p-6" style={{ backgroundColor: settings.primary_color }}>
              <h4 className="font-semibold mb-4" style={{ color: settings.secondary_color }}>Riepilogo Sistema</h4>
              <p className="text-lg">
                Per ogni <strong style={{ color: settings.secondary_color }}>€{settings.euros_per_point}</strong> spesi, il cliente guadagna <strong style={{ color: settings.secondary_color }}>1 punto</strong>.
                <br />
                Raggiungendo <strong style={{ color: settings.secondary_color }}>{settings.points_for_discount} punti</strong>, ottiene uno sconto di <strong style={{ color: settings.secondary_color }}>€{settings.discount_value}</strong>.
              </p>
            </div>
          </div>
        )}

        {/* Save Button (Floating on Mobile) */}
        <div className="fixed bottom-6 right-6 lg:hidden">
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-white px-6 py-4 rounded-full font-semibold shadow-2xl flex items-center gap-2"
            style={{ backgroundColor: settings.primary_color }}
          >
            <Save size={20} />
            {saving ? 'Salvataggio...' : `Salva ${SECTION_SAVE_LABELS[activeSection]}`}
          </button>
        </div>
      </main>
    </div>
  );
}
